import { Injectable } from '@angular/core';
import { STORAGE_KEYS } from "@core/storage/indexedDb/_database.constants";

export interface IndexedDbData<T> {
  data: T,
  synced: number,
  finalized: number,
  id: number,
  deleted: number
}

@Injectable({
  providedIn: 'root',
})
export abstract class BaseIndexedDbService<T> {
  abstract objectStoreName: string;
  #dbName = 'PKPDatabase'; // Az adatbázis neve
  #dbVersion = 6; // Az adatbázis verziója (6: work-time-entry store - E1.5)
  #db!: IDBDatabase; // Az IndexedDB referencia
  #isInitialized = false; // Inicializáció állapota

  protected constructor() {
    this.initializeDatabase().then();
  }

  /**
   * Adat hozzáadása az adott táblához
   */
  // public addItem(item: any, transaction?: IDBTransaction): Observable<number> {
  //   return this.executeDbOperation((store) => store.add({data: item, synced: false, finalized: false}), transaction);
  // }
  public async addItem(item: T, id?: number, transaction?: IDBTransaction): Promise<number> {
    return await this.executeDbOperation((store) => store.add(
      id !== undefined
        ? {data: item, synced: 0, finalized: 0, deleted: 0, id: id}
        : {data: item, synced: 0, finalized: 0, deleted: 0}
    ), transaction);
  }

  /**
   * Adat lekérdezése egyedi kulcs alapján
   */
  public async getItem(id: number = 0, transaction?: IDBTransaction): Promise<IndexedDbData<T>> {
    return this.executeDbOperation((store) => store.get(id), transaction);
  }

  /**
   * Összes adat lekérdezése az adott táblából, ami nem törölt
   */
  public getAllItems(transaction?: IDBTransaction): Promise<IndexedDbData<T>[]> {
    return this.executeDbOperation(
      (store) => store.getAll(),
      transaction,
      (request) => request.result.filter((x: IndexedDbData<T>) => x.deleted !== 1)
    );
  }

  /**
   * Összes adat lekérdezése az adott táblából
   */
  public getFullDb(transaction?: IDBTransaction): Promise<IndexedDbData<T>[]> {
    return this.executeDbOperation(
      (store) => store.getAll(),
      transaction
    );
  }

  public getUnfinalizedItems(transaction?: IDBTransaction): Promise<IndexedDbData<T>[]> {
    return this.executeDbOperation(
      (store) => store.index('finalized').getAll(IDBKeyRange.only(0)),
      transaction,
      (request) => request.result.filter((x: IndexedDbData<T>) => x.deleted !== 1));
  }

  public getUnsyncedItems(transaction?: IDBTransaction): Promise<IndexedDbData<T>[]> {
    return this.executeDbOperation(
      (store) => store.index('synced').getAll(IDBKeyRange.only(0)),
      transaction,
      // A logikailag torolt tetelt nem toltjuk fel: a felhasznalo torolte, a rekord csak
      // a db-dumper (5 tapintasos) feltolteshez marad meg az eszkozon.
      (request) => request.result.filter((item: IndexedDbData<T>) => item.finalized === 1 && item.deleted !== 1));
  }

  /**
   * Adat frissítése az adott táblában
   */
  // eslint-disable-next-line
  public updateItem(item: any, transaction?: IDBTransaction): Promise<number> {
    // Csak akkor allitjuk be, ha a hivo nem adott erteket (regi, `deleted` mezo nelkuli
    // rekordok es a mezot nem tartalmazo objektum-literalok miatt). Korabban ez feltetel
    // nelkul 0-ra allt, ezert a `deleteItem` torlese azonnal visszavonodott.
    item.deleted = item.deleted ?? 0;
    return this.executeDbOperation((store) => store.put(item), transaction);
  }

  /**
   * Adat törlése egyedi kulcs alapján az adott táblában
   */
  public async deleteItem(id: number, transaction?: IDBTransaction): Promise<number | null> {
    const item = await this.getItem(id, transaction);
    if (!item) {
      return null;
    }

    item.deleted = 1;
    // Szandekosan nem az `updateItem`-en keresztul: ott a `deleted` alapertelmezese van.
    return this.executeDbOperation((store) => store.put(item), transaction);
  }

  /**
   * Összes adat törlése az adott táblából
   */
  public clear(transaction?: IDBTransaction): Promise<void> {
    return this.executeDbOperation((store) => store.clear(), transaction);
  }

  /**
   * Több táblát érintő tranzakció kezelése
   */
  public async transaction(
    mode: IDBTransactionMode,
    operations: (transaction: IDBTransaction) => Promise<void>
  ): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.#db.transaction([this.objectStoreName], mode);

      transaction.oncomplete = () => {
        console.log('Transaction completed successfully.');
        resolve();
      };

      transaction.onerror = (event) => {
        console.error('Transaction failed:', event);
        reject(transaction.error);
      };

      transaction.onabort = (event) => {
        console.error('Transaction aborted:', event);
        reject(transaction.error);
      };

      // Az operációkat a tranzakcióra alkalmazzuk
      operations(transaction).catch((error) => {
        console.error('Error during transaction operations:', error);
        transaction.abort(); // Hibás operáció esetén a tranzakció megszakítása
      });
    });
  }

  /**
   * Adatbázis inicializálása
   */
  private async initializeDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.#dbName, this.#dbVersion);

      // eslint-disable-next-line
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result as IDBDatabase;
        console.log('Upgrading or creating database:', this.#dbName);
        Object.values(STORAGE_KEYS).forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, {keyPath: 'id', autoIncrement: true});
            store.createIndex('id', 'id', {unique: true});
            store.createIndex('synced', 'synced', {unique: false});
            store.createIndex('finalized', 'finalized', {unique: false});
            store.createIndex('deleted', 'deleted', {unique: false});
          } else {
            const store = request.transaction!.objectStore(storeName);
            if (!store.indexNames.contains('deleted')) {
              store.createIndex('deleted', 'deleted', { unique: false });
            }
            const getAllRequest = store.getAll();
            getAllRequest.onsuccess = () => {
              const records = getAllRequest.result;
              records.forEach((rec: IndexedDbData<T>) => {
                if (rec.deleted === undefined) {
                  rec.deleted = 0;
                  store.put(rec);
                }
              });
            };
          }
        });

      };

      // eslint-disable-next-line
      request.onsuccess = (event: any) => {
        this.#db = event.target.result as IDBDatabase;
        this.#isInitialized = true;
        console.log('Database initialized automatically:', this.#dbName, 'Object:', this.objectStoreName);
        resolve();
      };

      request.onerror = (event) => {
        console.error('Database initialization failed:', event);
        reject(event);
      };
    });
  }

  /**
   * Ellenőrzi, hogy az adatbázis inicializálva van-e
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.#isInitialized) {
      await this.initializeDatabase();
    }
  }

  private async createTransaction(mode: IDBTransactionMode): Promise<IDBTransaction> {
    await this.ensureInitialized();
    return this.#db.transaction([this.objectStoreName], mode);
  }

  // eslint-disable-next-line
  private async executeDbOperation(operation: (store: IDBObjectStore) => IDBRequest, transaction?: IDBTransaction, filter?: (request: IDBRequest) => any): Promise<any> {
    await this.ensureInitialized();
    return new Promise((resolve, reject) => {
      try {
        if (!transaction) {
          transaction = this.#db.transaction(this.objectStoreName, 'readwrite');
        }
        const store = transaction.objectStore(this.objectStoreName);
        const request = operation(store);

        // eslint-disable-next-line
        request.onsuccess = (event: any) => {
          resolve(filter ? filter(event.target) : event.target.result);
        };

        request.onerror = (event) => {
          console.error('Get item failed:', event);
          reject(event);
        };
        transaction.onabort = () => {
          console.error('Transaction aborted');
          reject(new Error('Transaction aborted'));
        };

        // eslint-disable-next-line
        transaction.onerror = (event: any) => {
          console.error('Transaction error:', event);
          reject(event.target.error);
        };
      } catch (error) {
        console.error('Unexpected error:', error);
        reject(error);
      }
    });

  }
}
